import React from "react";
import { motion } from "framer-motion";
import { CardType } from "../hooks/use-pairs-game";

interface CardProps {
  card: CardType;
  onClick: (id: number) => void;
  className?: string;
  disabled?: boolean;
  isGameOver: boolean;
  gridSize: number;
}

export default function Card({
  card,
  onClick,
  className = "",
  isGameOver,
  disabled,
  gridSize,
}: CardProps) {
  const isFaceUp = Boolean(card.isFlipped || card.isMatched);

  return (
    <motion.button
      animate={{
        scale: [0.95, 1.05, 1],
        opacity: [0.18, 0.5, 1],
      }}
      onClick={() => (disabled ? null : onClick(card.id))}
      disabled={card.isMatched}
      aria-pressed={isFaceUp}
      className={`w-full h-full aspect-square relative rounded-md  focus:outline-none ${className}`}
      // global perspective for the 3D effect
      style={{ perspective: 1200 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* rotating wrapper */}
      <motion.div
        // rotate the whole card in 3D
        animate={{
          rotateY: isFaceUp ? 180 : 0,
          scale: isFaceUp ? 1.02 : 1,
          boxShadow: isFaceUp
            ? "0 28px 50px rgba(51,108,119,0.35)"
            : "0 8px 20px rgba(0,0,0,0.18)",
        }}
        transition={{ duration: 0.66, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformStyle: "preserve-3d" }}
        className={`
          w-full h-full rounded-md relative select-none
          bg-transparent
       
          ${card.isMatched ? "ring-4 ring-[#56F5FF]/20" : ""}
        `}
      >
        {/* BACK FACE (when card is face-down) */}
        <div
          className={`
            absolute inset-0 rounded-md flex items-center justify-center
            border-[1px] border-white/25  ${gridSize > 6 ? "p-2" : "p-4"}
            bg-[#686b71]/60
            transform
               ${
                 isGameOver &&
                 !card.isMatched &&
                 "!bg-[#FF5656]/35 border-[#FF5656]/50"
               }
          `}
          style={{
            // visible when rotateY is 0
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          <div
            className={`rounded-md border-[1px] w-full h-full flex items-center justify-center  ${
              gridSize > 6 ? "text-xl" : "text-3xl"
            } ${
              isGameOver && !card.isMatched ? "border-none" : "border-white/15"
            }`}
          >
            {isGameOver && !card.isMatched && card.value}
          </div>
        </div>

        {/* FRONT FACE (when card is revealed) */}
        <div
          className={`
            absolute inset-0 rounded-md flex items-center justify-center  border-[1px]  ${
              gridSize > 6 ? "p-2" : "p-4"
            }
    
           ${
             card.isMatched
               ? "bg-[#56F5FF]/15 border-[#56F5FF]/60"
               : "bg-white/45 border-white/60"
           }
          `}
          style={{
            // this face is rotated so it shows after wrapper rotates 180deg
            transform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          {/* animated shine sweep — shown only when face up */}
          {isFaceUp && (
            <motion.div
              initial={{ x: "-110%", opacity: 0.0 }}
              animate={{ x: "120%", opacity: 0.18 }}
              transition={{
                duration: 0.9,
                ease: "easeOut",
              }}
              style={{
                position: "absolute",
                top: "-30%",
                left: 0,
                width: "60%",
                height: "160%",
                transform: "rotate(25deg)",
                background:
                  "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 100%)",
                filter: "blur(10px)",
                pointerEvents: "none",
                mixBlendMode: "screen",
                borderRadius: 12,
                zIndex: 2,
              }}
            />
          )}

          {/* subtle pulse / sparkle when matched */}
          {card.isMatched && (
            <motion.div
              initial={{ scale: 0.95, opacity: 0.18 }}
              animate={{
                scale: [0.95, 1.05, 0.98],
                opacity: [0.18, 0.5, 0.18],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 12,
                pointerEvents: "none",
                boxShadow: "0 0 60px rgba(86,245,255,0.12)",
                zIndex: 3,
              }}
            />
          )}

          {/* content of the front */}
          <div
            className={`relative z-10 flex-col gap-2 rounded-md w-full h-full flex items-center justify-center ${
              card.isMatched ? "border-none" : "border-white/60  border-[1px]"
            } `}
          >
            <div
              className={`select-none ${
                gridSize > 6 ? "text-2xl" : "text-4xl"
              }`}
            >
              {card.value}
            </div>
            {card.isMatched && (
              <div className="text-xs uppercase tracking-wide text-[#dffafa]">
                matched
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.button>
  );
}
